import React, { useEffect, useState } from "react"
import { HiddenInput, SelectInput } from "./Form";
import { checkQuantity } from "../withOrder";

const updateOrderDeliveryFee = (orderId, deliveryFeeOverride, deliveryFee, deliveryRequired) => {
  const body = new FormData()
  body.append("order[delivery_fee_override]", deliveryFeeOverride ? "1" : "0")
  if (deliveryFeeOverride) {
    body.append("order[delivery_fee]", deliveryFee)
  }
  body.append("delivery_required", deliveryRequired ? "1" : "0")

  return fetch(`/orders/${orderId}`, {
    body: body,
    method: "PATCH",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").getAttribute("content"),
    },
  })
    .then(response => response.json())
    .catch(error => {
      console.error('Error updating delivery fee:', error)
    })
}

const parseFee = (feeString) => (feeString || '').toString().replace(/[^0-9.]/g, '')

function OrderLineItem(props) {
  return (
    <tr>
      <td colSpan={2}>
        {props.vintageYear} {props.wineName} {props.size}
      </td>
      <td className="text-center">{props.price}</td>
      <td className="text-center">
        {props.editable ? (
          <>
            <HiddenInput
              name={`order[order_details_attributes][${props.id}][id]`}
              defaultValue={props.id}
            />
            <SelectInput
              name={`order[order_details_attributes][${props.id}][quantity]`}
              defaultValue={props.quantity}
              onChange={(e) => props.handleQuantityChange(props.id, e.target.value)}
              errors={props.quantityCheckError || props.errors.quantity}
              includeBlank={false}
              options={[...Array(200).keys()].map(i => [i, i])}
            />
          </>
        ) : props.quantity}
      </td>
      <td className="text-right">{props.total}</td>
    </tr>
  )
}

function OrderTotal(props) {
  return (
    <tr className="order-total">
      <td className="cell-label cell-label--number" colSpan="4">
        <strong>{props.name}</strong>
      </td>
      <td className="cell-data cell-data--number">{props.value}</td>
    </tr>
  )
}

function DeliveryFeeRow(props) {
  return (
    <tr className="order-total">
      <td className="cell-label cell-label--number" colSpan="4">
        <ul className="delivery-fee-controls">
          {props.showOverrideCheckbox && (
            <li className="mr-2">
              <div className="checkbox">
                <input
                  type="hidden"
                  name="order[delivery_fee_override]"
                  value="0"
                />
                <label>
                  <input
                    type="checkbox"
                    name="order[delivery_fee_override]"
                    value="1"
                    checked={props.deliveryFeeOverride}
                    onChange={props.onOverrideChange}
                  />
                  Override
                </label>
              </div>
            </li>
          )}
          {props.showOverrideCheckbox && (
            <li className="mr-1">|</li>
          )}
          <li><strong>Delivery Fee</strong></li>
        </ul>
      </td>
      <td className="cell-data cell-data--number">
        {props.deliveryRequired && props.deliveryFeeOverride ? (
          <div>
            <input
              type="number"
              name="order[delivery_fee]"
              value={props.deliveryFeeInputValue}
              onChange={props.onDeliveryFeeChange}
              onBlur={props.onDeliveryFeeBlur}
              className='delivery-fee-input'
              step="0.01"
            />
          </div>
        ) : (
          <div>
            {props.deliveryFee}
          </div>
        )}
      </td>
    </tr>
  )
}

export default function FoodTableOrderTable(props) {
  const {
    id,
    deliveryFee: initialDeliveryFee,
    grandTotal: initialGrandTotal,
    deliveryFeeOverride: initialDeliveryFeeOverride,
    deliveryRequired,
    editable,
    orderDetails,
    handleOrderUpdate,
  } = props

  const [deliveryFeeOverride, setDeliveryFeeOverride] = useState(false)
  const [deliveryFee, setDeliveryFee] = useState('$0.00')
  const [deliveryFeeInput, setDeliveryFeeInput] = useState("0.00")
  const [grandTotal, setGrandTotal] = useState('$0.00')
  const [isSaving, setIsSaving] = useState(false)
  const [quantityCheckErrors, setQuantityCheckErrors] = useState({})

  useEffect(() => {
    setDeliveryFee(initialDeliveryFee || '$0.00')
    setGrandTotal(initialGrandTotal || '$0.00')
    setDeliveryFeeOverride(initialDeliveryFeeOverride || false)
  }, [initialDeliveryFee, initialGrandTotal, initialDeliveryFeeOverride])

  useEffect(() => {
    setDeliveryFeeInput(parseFee(deliveryFee))
  }, [deliveryFee])

  useEffect(() => {
    if (!editable) return

    if (deliveryRequired === false) {
      if (parseFee(deliveryFee) !== '0' && parseFee(deliveryFee) !== '0.00') {
        setIsSaving(true)
        updateOrderDeliveryFee(id, false, 0, deliveryRequired)
          .then(handleUpdateResponse)
          .finally(() => setIsSaving(false))
      }
    }
  }, [deliveryRequired, id, deliveryFee, editable])

  const handleUpdateResponse = (data) => {
    if (data) {
      setDeliveryFee(data.order.deliveryFee)
      setGrandTotal(data.order.grandTotal)
      setDeliveryFeeOverride(data.order.deliveryFeeOverride)
    }
  }

  const handleOverrideChange = (e) => {
    const isOverriding = e.target.checked
    setDeliveryFeeOverride(isOverriding)

    if (!isOverriding) {
      setIsSaving(true)
      updateOrderDeliveryFee(id, false, '0', true)
        .then(handleUpdateResponse)
        .finally(() => setIsSaving(false))
    }
  }

  const handleDeliveryFeeChange = (e) => {
    const value = e.target.value
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setDeliveryFeeInput(value)
    }
  }

  const handleDeliveryFeeBlur = () => {
    if (deliveryFeeOverride) {
      setIsSaving(true)
      updateOrderDeliveryFee(id, true, deliveryFeeInput, true)
        .then(handleUpdateResponse)
        .finally(() => setIsSaving(false))
    }
  }

  const handleQuantityChange = (orderDetailId, quantity) => {
    checkQuantity(id, orderDetailId, quantity).then(response => {
      if (response.valid) {
        setQuantityCheckErrors(prev => {
          const next = { ...prev }
          delete next[orderDetailId]
          return next
        })
        handleOrderUpdate({})
      } else {
        const result = (response.results || []).find(
          r => String(r.order_detail_id) === String(orderDetailId)
        )
        if (result) {
          setQuantityCheckErrors(prev => ({
            ...prev,
            [orderDetailId]: `Only ${result.available_quantity} available`,
          }))
        }
      }
    })
  }

  return (
    <table className="table table-striped table-hover allocation-table">
      <thead>
      <tr>
        <th colSpan={2}>Product</th>
        <th className="text-center">Price</th>
        <th className="col-xs-1 cell-label cell-label--number text-center">Quantity</th>
        <th className="col-xs-1 cell-label cell-label--number text-right">Total Price</th>
      </tr>
      </thead>
      <tbody>
      {orderDetails.map(lineItem => <OrderLineItem
        key={lineItem.id}
        handleQuantityChange={handleQuantityChange}
        quantityCheckError={quantityCheckErrors[lineItem.id]}
        editable={editable}
        {...lineItem}
      />)}
      <DeliveryFeeRow
        showOverrideCheckbox={deliveryRequired && editable}
        deliveryFeeOverride={deliveryFeeOverride}
        deliveryFee={deliveryFee}
        deliveryFeeInputValue={deliveryFeeInput}
        onOverrideChange={handleOverrideChange}
        onDeliveryFeeChange={handleDeliveryFeeChange}
        onDeliveryFeeBlur={handleDeliveryFeeBlur}
        deliveryRequired={deliveryRequired && editable}
      />
      <OrderTotal name="Grand Total inc. GST" value={grandTotal} />
      </tbody>
    </table>
  )
}
